import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { ownerLicenseFormQuery, saveOwnerLicense, type OwnerLicenseSaved } from '../../api/settingsLicenses'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * "Add license" / "Edit license" pe un contact - macheta
 * `settings/licenses/owners_support/licenseForm.php` + `licenseForm-1.js`,
 * si `ownerSupportLicenseForm` / `updateOwnerSupport`.
 *
 * Aceleasi doua campuri: tipul (SKU-ul, cu pretul si taxa recurenta in
 * paranteza, ca in `<option>`) si "License start" - a carei eticheta spune
 * perioada tipului ales (`data-period`): "(N days period)" sau
 * "(unlimited period)", cand casuta se si inchide. Formularul vechi n-avea
 * "License end": o pune serverul din `support_days`.
 *
 * Dupa salvare, serverul spune ce a facut cu actiunile tipului (tichetul cu
 * SKU): fiecare isi are randul lui in toastr, ca omul sa vada ca licenta e
 * pusa si tichetul nu, cand e cazul.
 */
export function OwnerLicenseModal({ ownerId, onClose, onSaved }: { ownerId: number; onClose(): void; onSaved(): void }) {
  const form = useQuery(ownerLicenseFormQuery(ownerId))
  const [type, setType] = useState('')
  const [start, setStart] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!form.data) return
    setType(form.data.license ? String(form.data.license.license_type) : '')
    setStart(form.data.license?.start_license || form.data.today)
  }, [form.data])

  const types = form.data?.types ?? []
  const chosen = types.find((one) => String(one.id) === type)
  const unlimited = chosen?.period === -1
  const startLabel =
    chosen === undefined
      ? 'License start:'
      : unlimited
        ? 'License start (unlimited period):'
        : `License start (${chosen.period} days period):`

  const submit = () => {
    setBusy(true)
    void saveOwnerLicense(ownerId, { license_type: type, license_start: unlimited ? '' : start })
      .then((saved: OwnerLicenseSaved) => {
        setBusy(false)
        toast('License saved.', 'Success')
        for (const action of saved.actions) {
          if (action.ticket > 0) toast(`Ticket #${action.ticket} opened for SKU ${action.sku}.`, 'License', 'success')
          else toast(`SKU ${action.sku}: ${action.error}`, 'License', 'error')
        }
        onSaved()
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error')
      })
  }

  return (
    <Modal id="editLicenseModal" open={ownerId > 0} onClose={onClose} title="License" labelledBy="editLicenseModalLabel">
      {form.error instanceof ApiError ? (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {form.error.message || 'Not found.'}
        </div>
      ) : !form.data ? (
        <Spinner />
      ) : (
        <form method="post" className="owner_license_form" id="owner_license_form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label className="control-label" htmlFor="owner-license-type">
              License type <span className="required">*</span>
            </label>
            <select
              id="owner-license-type"
              className="form-control change-license"
              name="license_type"
              required
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Select...</option>
              {types.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.skuName} ( {one.cost} {one.currency}
                  {one.recurringCost !== '' ? ` + ${one.recurringCost} ${one.recurringCurrency} Recurring fee` : ''} )
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="control-label" id="license_start_label" htmlFor="license_start">
              {startLabel}
            </label>
            <input
              type="date"
              className="form-control license-range"
              id="license_start"
              name="license_start"
              disabled={unlimited}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="button" className="btn btn-primary" id="saveOwnerSupport" disabled={busy || type === ''} onClick={submit}>
              Save
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
